import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Shield,
  Plus,
  Edit,
  Trash2,
  Check,
  X,
  Eye,
  PenLine,
  UserCog,
  Lock,
  Unlock,
} from "lucide-react";

const API = `${window.location.origin}/api`;

export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    permissions: [],
    is_active: true,
  });

  useEffect(() => {
    fetchRoles();
    fetchModules();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data);
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("فشل في تحميل الأدوار");
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/roles/modules`);
      setModules(response.data);
    } catch (error) {
      console.error("Error fetching modules:", error);
    }
  };

  const handleOpenDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name,
        description: role.description || "",
        permissions: role.permissions || [],
        is_active: role.is_active !== false,
      });
    } else {
      setEditingRole(null);
      // إنشاء صلاحيات فارغة لكل وحدة
      const emptyPermissions = modules.map((m) => ({
        module: m.id,
        can_view: false,
        can_create: false,
        can_edit: false,
        can_delete: false,
      }));
      setFormData({
        name: "",
        description: "",
        permissions: emptyPermissions,
        is_active: true,
      });
    }
    setIsDialogOpen(true);
  };

  const handlePermissionChange = (moduleId, permType, value) => {
    setFormData((prev) => {
      const newPermissions = prev.permissions.map((p) => {
        if (p.module === moduleId) {
          // إذا تم إلغاء "عرض"، ألغِ كل الصلاحيات الأخرى
          if (permType === "can_view" && !value) {
            return { ...p, can_view: false, can_create: false, can_edit: false, can_delete: false };
          }
          // إذا تم تفعيل أي صلاحية أخرى، فعّل "عرض" تلقائياً
          if (permType !== "can_view" && value) {
            return { ...p, [permType]: value, can_view: true };
          }
          return { ...p, [permType]: value };
        }
        return p;
      });
      return { ...prev, permissions: newPermissions };
    });
  };

  const handleSelectAll = (moduleId, selectAll) => {
    setFormData((prev) => {
      const newPermissions = prev.permissions.map((p) => {
        if (p.module === moduleId) {
          return {
            ...p,
            can_view: selectAll,
            can_create: selectAll,
            can_edit: selectAll,
            can_delete: selectAll,
          };
        }
        return p;
      });
      return { ...prev, permissions: newPermissions };
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("يرجى إدخال اسم الدور");
      return;
    }

    try {
      if (editingRole) {
        await axios.put(`${API}/roles/${editingRole.id}`, formData);
        toast.success("تم تحديث الدور بنجاح");
      } else {
        await axios.post(`${API}/roles`, formData);
        toast.success("تم إنشاء الدور بنجاح");
      }
      setIsDialogOpen(false);
      fetchRoles();
    } catch (error) {
      console.error("Error saving role:", error);
      toast.error(error.response?.data?.detail || "فشل في حفظ الدور");
    }
  };

  const handleDelete = async (roleId) => {
    const role = roles.find((r) => r.id === roleId);
    if (role?.is_default) {
      toast.error("لا يمكن حذف الأدوار الافتراضية");
      return;
    }

    if (!window.confirm("هل أنت متأكد من حذف هذا الدور؟")) {
      return;
    }

    try {
      await axios.delete(`${API}/roles/${roleId}`);
      toast.success("تم حذف الدور بنجاح");
      fetchRoles();
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error(error.response?.data?.detail || "فشل في حذف الدور");
    }
  };

  const getModuleName = (moduleId) => {
    return modules.find((m) => m.id === moduleId)?.name || moduleId;
  };

  const countPermissions = (permissions) => {
    let count = 0;
    permissions?.forEach((p) => {
      if (p.can_view) count++;
      if (p.can_create) count++;
      if (p.can_edit) count++;
      if (p.can_delete) count++;
    });
    return count;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="roles-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-7 h-7 text-purple-500" />
            أدوار الموظفين
          </h1>
          <p className="text-slate-400 text-sm mt-1">إدارة الأدوار والصلاحيات للموظفين</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="add-role-btn"
        >
          <Plus className="w-4 h-4 me-2" />
          دور جديد
        </Button>
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((role) => (
          <Card
            key={role.id}
            className={`bg-slate-800/50 border-slate-700 hover:border-purple-500/50 transition-colors ${
              !role.is_active ? "opacity-60" : ""
            }`}
          >
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className={`p-2 rounded-lg ${
                      role.is_default ? "bg-purple-500/20" : "bg-slate-700"
                    }`}
                  >
                    {role.is_default ? (
                      <Lock className="w-4 h-4 text-purple-400" />
                    ) : (
                      <Unlock className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <span className="text-white">{role.name}</span>
                    {role.is_default && (
                      <span className="text-xs text-purple-400 block">افتراضي</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleOpenDialog(role)}
                    className="h-8 w-8 text-slate-400 hover:text-white"
                    title={role.is_default ? "عرض" : "تعديل"}
                  >
                    {role.is_default ? <Eye className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
                  </Button>
                  {!role.is_default && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDelete(role.id)}
                      className="h-8 w-8 text-red-400 hover:text-red-300"
                      title="حذف"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {role.description && (
                <p className="text-slate-400 text-sm mb-3">{role.description}</p>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">الصلاحيات:</span>
                <span className="text-purple-400 font-bold">
                  {countPermissions(role.permissions)} صلاحية
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {role.permissions?.slice(0, 4).map((p) =>
                  p.can_view ? (
                    <span
                      key={p.module}
                      className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs"
                    >
                      {getModuleName(p.module)}
                    </span>
                  ) : null
                )}
                {role.permissions?.filter((p) => p.can_view).length > 4 && (
                  <span className="px-2 py-1 bg-slate-700 text-slate-400 rounded text-xs">
                    +{role.permissions.filter((p) => p.can_view).length - 4}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add/Edit Role Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <UserCog className="w-5 h-5 text-purple-500" />
              {editingRole
                ? editingRole.is_default
                  ? `عرض الدور: ${editingRole.name}`
                  : `تعديل الدور: ${editingRole.name}`
                : "إنشاء دور جديد"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">اسم الدور *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="مثال: مدير المبيعات"
                  className="bg-slate-800 border-slate-700"
                  disabled={editingRole?.is_default}
                  data-testid="role-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">الوصف</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="وصف مختصر للدور"
                  className="bg-slate-800 border-slate-700"
                  disabled={editingRole?.is_default}
                />
              </div>
            </div>

            {/* Permissions Table */}
            <div className="space-y-3">
              <Label className="text-slate-300 text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-500" />
                الصلاحيات
              </Label>
              <div className="rounded-lg border border-slate-700 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700 bg-slate-800/50 hover:bg-slate-800/50">
                      <TableHead className="text-slate-300 w-48">الوحدة</TableHead>
                      <TableHead className="text-slate-300 text-center w-24">
                        <div className="flex items-center justify-center gap-1">
                          <Eye className="w-4 h-4" />
                          عرض
                        </div>
                      </TableHead>
                      <TableHead className="text-slate-300 text-center w-24">
                        <div className="flex items-center justify-center gap-1">
                          <Plus className="w-4 h-4" />
                          إنشاء
                        </div>
                      </TableHead>
                      <TableHead className="text-slate-300 text-center w-24">
                        <div className="flex items-center justify-center gap-1">
                          <PenLine className="w-4 h-4" />
                          تعديل
                        </div>
                      </TableHead>
                      <TableHead className="text-slate-300 text-center w-24">
                        <div className="flex items-center justify-center gap-1">
                          <Trash2 className="w-4 h-4" />
                          حذف
                        </div>
                      </TableHead>
                      <TableHead className="text-slate-300 text-center w-24">الكل</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {modules.map((module) => {
                      const perm = formData.permissions.find((p) => p.module === module.id) || {
                        can_view: false,
                        can_create: false,
                        can_edit: false,
                        can_delete: false,
                      };
                      const allSelected =
                        perm.can_view && perm.can_create && perm.can_edit && perm.can_delete;

                      return (
                        <TableRow key={module.id} className="border-slate-700 hover:bg-slate-800/30">
                          <TableCell className="text-white font-medium">{module.name}</TableCell>
                          <TableCell className="text-center">
                            <button
                              type="button"
                              onClick={() =>
                                !editingRole?.is_default &&
                                handlePermissionChange(module.id, "can_view", !perm.can_view)
                              }
                              disabled={editingRole?.is_default}
                              className={`p-2 rounded transition-colors ${
                                perm.can_view
                                  ? "bg-green-500/20 text-green-400"
                                  : "bg-slate-700 text-slate-500"
                              } ${editingRole?.is_default ? "cursor-not-allowed" : "hover:opacity-80"}`}
                            >
                              {perm.can_view ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                            </button>
                          </TableCell>
                          <TableCell className="text-center">
                            <button
                              type="button"
                              onClick={() =>
                                !editingRole?.is_default &&
                                handlePermissionChange(module.id, "can_create", !perm.can_create)
                              }
                              disabled={editingRole?.is_default}
                              className={`p-2 rounded transition-colors ${
                                perm.can_create
                                  ? "bg-blue-500/20 text-blue-400"
                                  : "bg-slate-700 text-slate-500"
                              } ${editingRole?.is_default ? "cursor-not-allowed" : "hover:opacity-80"}`}
                            >
                              {perm.can_create ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                            </button>
                          </TableCell>
                          <TableCell className="text-center">
                            <button
                              type="button"
                              onClick={() =>
                                !editingRole?.is_default &&
                                handlePermissionChange(module.id, "can_edit", !perm.can_edit)
                              }
                              disabled={editingRole?.is_default}
                              className={`p-2 rounded transition-colors ${
                                perm.can_edit
                                  ? "bg-amber-500/20 text-amber-400"
                                  : "bg-slate-700 text-slate-500"
                              } ${editingRole?.is_default ? "cursor-not-allowed" : "hover:opacity-80"}`}
                            >
                              {perm.can_edit ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                            </button>
                          </TableCell>
                          <TableCell className="text-center">
                            <button
                              type="button"
                              onClick={() =>
                                !editingRole?.is_default &&
                                handlePermissionChange(module.id, "can_delete", !perm.can_delete)
                              }
                              disabled={editingRole?.is_default}
                              className={`p-2 rounded transition-colors ${
                                perm.can_delete
                                  ? "bg-red-500/20 text-red-400"
                                  : "bg-slate-700 text-slate-500"
                              } ${editingRole?.is_default ? "cursor-not-allowed" : "hover:opacity-80"}`}
                            >
                              {perm.can_delete ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                            </button>
                          </TableCell>
                          <TableCell className="text-center">
                            <button
                              type="button"
                              onClick={() =>
                                !editingRole?.is_default && handleSelectAll(module.id, !allSelected)
                              }
                              disabled={editingRole?.is_default}
                              className={`px-3 py-1 rounded text-xs transition-colors ${
                                allSelected
                                  ? "bg-purple-500/20 text-purple-400"
                                  : "bg-slate-700 text-slate-400"
                              } ${editingRole?.is_default ? "cursor-not-allowed" : "hover:opacity-80"}`}
                            >
                              {allSelected ? "إلغاء" : "تحديد"}
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              className="border-slate-600 text-slate-300"
            >
              {editingRole?.is_default ? "إغلاق" : "إلغاء"}
            </Button>
            {!editingRole?.is_default && (
              <Button
                onClick={handleSubmit}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="submit-role-btn"
              >
                <Check className="w-4 h-4 me-2" />
                {editingRole ? "تحديث" : "إنشاء"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
